/**
 * components/BayarTagihanModal.js
 * "Bayar Tagihan" — record additional payments against a Belum Lunas transaction. Reachable
 * only when `txnStatus(txn) !== "LUNAS"` (Sisa Tagihan > 0).
 *
 * Payment-correction rework: this modal is ADD-ONLY again — correcting or removing an
 * EXISTING payment record moved to Edit Transaksi (`TransactionFormModal`, edit mode), the
 * ONE place full payment correction now lives. "Pembayaran Saat Ini" below is plain read-only
 * text, not an editable row.
 *
 * Layout, top to bottom: a read-only context card (Klien/Supplier/Pihak, item summary, Nilai
 * Total, Sisa Tagihan, Tanggal) — the CURRENT payment breakdown as plain text — a
 * "+ Tambah metode" rows editor for NEW payments, an auto-computed "akan ditandai Lunas"
 * indicator, and finally Batal / Konfirmasi Bayar (→ `onRecordPayment`, new rows only).
 *
 * The "akan ditandai Lunas" indicator is a PURE boolean computed during render — never its own
 * `useState` synced via `useEffect` — deliberately avoiding the exact post-paint-effect trap the
 * recent Lunas-mode keystroke-stutter fix (`TransactionFormModal.js`) had to remove.
 *
 * Payment-correction rework fix: "Pembayaran Saat Ini" rows also show each payment's own
 * `fmtDate(p.paidAt)` timestamp now, alongside the method and amount — without it, two payments
 * sharing a method were indistinguishable.
 *
 * paidAt feature: `paidAt` is a NEW field (a user-settable real-world payment date/time,
 * separate from `at`, which stays a pure server-stamped audit timestamp — unchanged, never
 * shown). The read-only line above switched from `fmtDate(p.at)` to `fmtDate(p.paidAt)`. The
 * "Tambah Pembayaran" new-rows editor below gained its own "Tanggal Bayar"/"Waktu Bayar"
 * date+time input pair per row (mirroring the transaction-level Tanggal/Waktu convention),
 * defaulting to `today()`/`nowTime()` on add — combined into a single ISO string via
 * `combineDateTime` only at submit time (`onRecordPayment`), never held as one combined value
 * while the row is being edited.
 *
 * paidAt duplicate-prevention feature: a new row is blocked (inline, before Konfirmasi Bayar
 * is even reachable) when its method+paidAt exactly match either an existing "Pembayaran Saat
 * Ini" row OR another new row in this same session — the full existing payment set, not just
 * what's currently typed. `utils/payments.js`'s `hasDuplicatePaymentAgainst` does the actual
 * comparison; amount never factors in, and two rows differing in date OR time stay allowed (a
 * legitimate backdated-payment scenario).
 *
 * Pembelian batch 2 (transfer support) — a new optional `perspective` prop, same fix as
 * `TransactionDetailModal.js`'s own identical one (see that file's own header comment for the
 * full reasoning): `contact` always names the TRANSFER's destination side; opened from
 * Pembelian's own books (`perspective==="PEMBELIAN"`), Pihak must name the source instead, via
 * `resolveLocationLabel(txn.location, txn.branchId, branches)`, never a contact. Untouched for
 * every other case, including PiutangUtang.js's own call site (perspective omitted).
 */
import React, { useState } from "react";
import { fmtQty, fmtDate, today, nowTime, combineDateTime } from "../utils/idGenerators";
import { txnTotal, txnSisa } from "../utils/txnStatus";
import { PAYMENT_METHODS, METHOD_LABEL, validatePaymentSet, paymentMethodText, hasDuplicatePaymentAgainst } from "../utils/payments";
import { tagihanPotonganOwed, hasTagihanPotonganItems, barangSisa, tagihanPotonganSisa, validateSplitSum, checkBucketOverAllocation, bucketOverAllocationMessage, paymentSplitLine } from "../utils/paymentSplit";
import { formatCurrencyInput, parseCurrencyInput } from "../utils/currency";
import { useModalEsc, backdropClose } from "../utils/modalStack";
import { displayContact } from "../utils/contacts";
import { resolveLocationLabel } from "../utils/branches";

const PARTY_LABEL = { PEMBELIAN: "Penjual", TRANSFER: "Pihak" };

export default function BayarTagihanModal({ txn, contacts = [], branches = [], perspective, onRecordPayment, onClose }) {
  useModalEsc(onClose);

  const [newRows, setNewRows] = useState([]);

  const contact = contacts.find((c) => c.id === txn.contactId) || null;
  // Contact model redesign leak fix: a TRANSFER's `contact` is always an app-maintained internal
  // stand-in whose `.name` is a fixed, never-real placeholder ("__internal__") — reading it
  // directly would show that literal string as "Pihak". `displayContact` resolves the real
  // branch name instead (or "" when it genuinely can't — never the raw field).
  // Pembelian batch 2 — see this file's own header comment: `contact` always names the
  // DESTINATION side, wrong once viewed from Pembelian's own books.
  const contactName = txn.type === "TRANSFER" && perspective === "PEMBELIAN"
    ? resolveLocationLabel(txn.location, txn.branchId, branches)
    : (contact ? displayContact(contact, branches) : "");
  const total = txnTotal(txn);
  const sisa = txnSisa(txn);
  const itemSummary = (txn.lines || [])
    .map((l) => `${l.name || l.internalCode} (${fmtQty(l.qty)})`)
    .join(", ") || "—";

  // Silent-defaults fix (2026-10-12): no method until one is picked (see TransactionFormModal).
  const addRow = () => setNewRows((rs) => [...rs, {
    method: "", amount: null, methodNote: null, paidAtDate: today(), paidAtTime: nowTime(),
  }]);
  const removeRow = (i) => setNewRows((rs) => rs.filter((_, idx) => idx !== i));
  const setRowMethod = (i, method) =>
    setNewRows((rs) => rs.map((r, idx) =>
      idx === i ? { ...r, method, methodNote: method === "LAINNYA" ? (r.methodNote || "") : null } : r
    ));
  const setRowAmount = (i, amount) => setNewRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, amount } : r)));
  const setRowNote = (i, methodNote) => setNewRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, methodNote } : r)));
  const setRowPaidAtDate = (i, paidAtDate) => setNewRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, paidAtDate } : r)));
  const setRowPaidAtTime = (i, paidAtTime) => setNewRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, paidAtTime } : r)));
  // Two-bucket payment split — never auto-filled, mirroring TransactionFormModal's own rule.
  const setRowSplitBarang = (i, splitBarang) => setNewRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, splitBarang } : r)));
  const setRowSplitTagihanPotongan = (i, splitTagihanPotongan) => setNewRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, splitTagihanPotongan } : r)));

  const newRowsTotal = newRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  // Pure, render-time only — see the file header comment on why this must never be a
  // useEffect-synced useState.
  const wouldBeLunas = newRows.length > 0 && sisa - newRowsTotal === 0;

  const paymentsCheck = validatePaymentSet([...(txn.payments || []), ...newRows], total);

  // paidAt duplicate-prevention feature: a new row is flagged when its method+paidAt exactly
  // match either an already-persisted payment on this transaction OR another new row being
  // added in this same session — the "full existing payment set" scope, not just what's typed
  // right now. Existing (read-only) rows already carry a real `paidAt`; new rows are combined
  // via `combineDateTime` the same way `submit()` below does, so this always checks the exact
  // value that would actually be persisted.
  const existingPaymentCandidates = (txn.payments || []).map((p) => ({ method: p.method, paidAt: p.paidAt }));
  const newRowCandidates = newRows.map((r) => ({
    method: r.method, paidAt: combineDateTime(r.paidAtDate, r.paidAtTime),
  }));
  const duplicateNewRowIndexes = new Set();
  newRowCandidates.forEach((cand, i) => {
    const others = [...existingPaymentCandidates, ...newRowCandidates.filter((_, idx) => idx !== i)];
    if (hasDuplicatePaymentAgainst(cand, others)) duplicateNewRowIndexes.add(i);
  });
  const hasDuplicatePayment = duplicateNewRowIndexes.size > 0;

  // Two-bucket payment split — only relevant once a live Tagihan & Potongan bucket exists on
  // this real, already-persisted transaction (no toggle in this modal — Check 2 doesn't apply
  // here, only the split-cap rule, point 3). `checkBucketOverAllocation` reads `txn.payments`
  // via `paymentSplit()`'s own legacy-fallback rule (a genuinely OLD row predating the bucket
  // correctly counts as 100% Barang here, unlike the create-form's own 0-default preview,
  // since these really are historical rows, not fresh in-progress ones).
  const hasBucket = tagihanPotonganOwed(txn) > 0;
  // 2nd-round follow-up fix: the read-only "Pembayaran Saat Ini" split line uses a narrower,
  // item-presence-based gate than `hasBucket` above — a real tagihan/potongan item can exist
  // even when the net is zero/negative (a discount outweighing a charge), and a real payment's
  // split should still be shown then. `hasBucket` itself stays untouched — it still governs the
  // "Rincian Pembagian" entry fields on new rows below, which is deliberately unaffected.
  const hasSplitLineItems = hasTagihanPotonganItems(txn);
  const allSplitsValid = !hasBucket || newRows.every((r) => validateSplitSum(r.amount, r.splitBarang, r.splitTagihanPotongan));
  const overAllocCheck = hasBucket && allSplitsValid
    ? checkBucketOverAllocation({
        ...txn,
        payments: [
          ...(txn.payments || []),
          ...newRows.map((r) => ({
            amount: Number(r.amount) || 0,
            splitBarang: Number(r.splitBarang) || 0,
            splitTagihanPotongan: Number(r.splitTagihanPotongan) || 0,
          })),
        ],
      })
    : { ok: true, violations: [] };

  const canSubmit = newRows.length > 0 && paymentsCheck.ok && !hasDuplicatePayment &&
    (!hasBucket || (allSplitsValid && overAllocCheck.ok));

  const submit = () => {
    if (!canSubmit) return;
    onRecordPayment(txn.id, newRows.map((r) => ({
      method: r.method, amount: Number(r.amount) || 0, methodNote: r.methodNote,
      paidAt: combineDateTime(r.paidAtDate, r.paidAtTime),
      ...(hasBucket
        ? { splitBarang: Number(r.splitBarang) || 0, splitTagihanPotongan: Number(r.splitTagihanPotongan) || 0 }
        : {}),
    })));
    onClose();
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
      <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Bayar Tagihan</h2>
        <p className="page-sub mono" style={{ marginBottom: "var(--gap-lg)" }}>{txn.invoiceNumber || txn.supplierInvoiceNo || "—"}</p>

        <dl className="detail-grid">
          <dt>{PARTY_LABEL[txn.type] || "Pembeli"}</dt><dd>{contactName || "—"}</dd>
          <dt>Barang</dt><dd>{itemSummary}</dd>
          <dt>Nilai Total</dt><dd>{fmtQty(total)}</dd>
          <dt>Tanggal</dt><dd>{txn.date}</dd>
        </dl>

        <div style={{ display: "flex", gap: "var(--gap-md)", marginTop: "var(--gap-lg)" }}>
          <div style={{ flex: 1, background: "var(--green-bg)", border: "1px solid #6ee7b7", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 12, color: "#065f46", fontWeight: 600, marginBottom: "var(--gap-xs)" }}>Sisa Barang</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#065f46" }}>Rp {fmtQty(barangSisa(txn))}</div>
          </div>
          <div style={{ flex: 1, background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 12, color: "#92400e", fontWeight: 600, marginBottom: "var(--gap-xs)" }}>Sisa Tagihan & Potongan</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#92400e" }}>Rp {fmtQty(tagihanPotonganSisa(txn))}</div>
          </div>
        </div>

        <div className="card" style={{ marginTop: "var(--gap-lg)" }}>
          <div className="card__title">Pembayaran Saat Ini</div>
          {(txn.payments || []).length === 0 && (
            <div className="form-hint">Belum ada pembayaran.</div>
          )}
          {(txn.payments || []).map((p) => (
            <div className="payment-row" key={p.id}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{paymentMethodText(p)} — {fmtDate(p.paidAt)}</span>
                <span>Rp {fmtQty(p.amount)}</span>
              </div>
              {hasSplitLineItems && (
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: "var(--gap-xs)" }}>{paymentSplitLine(p)}</div>
              )}
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card__title">Tambah Pembayaran</div>
          {newRows.length === 0 && (
            <div className="form-hint" style={{ marginBottom: "var(--gap-sm)" }}>Belum ada baris pembayaran baru.</div>
          )}
          {newRows.map((r, i) => (
            <div className="payment-row payment-row--editable" key={i} style={{ marginBottom: "var(--gap-sm)" }}>
              <div style={{ display: "flex", gap: "var(--gap-md)", alignItems: "flex-end" }}>
                <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`bayar-metode-${i}`}>Metode</label>
                  <select id={`bayar-metode-${i}`} className="form-select" value={r.method} onChange={(e) => setRowMethod(i, e.target.value)}>
                    {!r.method && <option value="" disabled>— pilih metode —</option>}
                    {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
                  </select>
                </div>
                <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`bayar-jumlah-${i}`}>Jumlah</label>
                  <div className="currency-input">
                    <span className="currency-input__prefix">Rp</span>
                    <input id={`bayar-jumlah-${i}`} type="text" inputMode="numeric" value={formatCurrencyInput(r.amount)}
                      onChange={(e) => setRowAmount(i, parseCurrencyInput(e.target.value))} />
                  </div>
                </div>
                <button type="button" className="btn btn-ghost btn-sm" title="Hapus baris" onClick={() => removeRow(i)}>✕</button>
              </div>
              {hasBucket && (
                <div style={{ marginTop: "var(--gap-sm)" }}>
                  <div className="form-row" style={{ marginBottom: "var(--gap-xs)" }}>
                    <label className="form-label">Rincian Pembagian</label>
                  </div>
                  <div style={{ display: "flex", gap: "var(--gap-md)", alignItems: "flex-end" }}>
                    <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                      <label className="form-label" htmlFor={`bayar-split-barang-${i}`}>Untuk Barang</label>
                      <div className="currency-input">
                        <span className="currency-input__prefix">Rp</span>
                        <input id={`bayar-split-barang-${i}`} type="text" inputMode="numeric" value={formatCurrencyInput(r.splitBarang)}
                          onChange={(e) => setRowSplitBarang(i, parseCurrencyInput(e.target.value))} />
                      </div>
                    </div>
                    <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                      <label className="form-label" htmlFor={`bayar-split-tagihan-${i}`}>Untuk Tagihan & Potongan</label>
                      <div className="currency-input">
                        <span className="currency-input__prefix">Rp</span>
                        <input id={`bayar-split-tagihan-${i}`} type="text" inputMode="numeric" value={formatCurrencyInput(r.splitTagihanPotongan)}
                          onChange={(e) => setRowSplitTagihanPotongan(i, parseCurrencyInput(e.target.value))} />
                      </div>
                    </div>
                  </div>
                  {validateSplitSum(r.amount, r.splitBarang, r.splitTagihanPotongan)
                    ? <div className="form-hint" style={{ color: "var(--green)" }}>✓ Sesuai</div>
                    : <div className="form-hint" style={{ color: "var(--red)" }}>Rincian pembagian harus berjumlah sama dengan Jumlah.</div>}
                </div>
              )}
              <div style={{ display: "flex", gap: "var(--gap-md)", alignItems: "flex-end", marginTop: "var(--gap-sm)" }}>
                <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`bayar-tgl-${i}`}>Tanggal Bayar</label>
                  <input id={`bayar-tgl-${i}`} className="form-input" type="date" value={r.paidAtDate}
                    onChange={(e) => setRowPaidAtDate(i, e.target.value)} />
                </div>
                <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`bayar-waktu-${i}`}>Waktu Bayar</label>
                  <input id={`bayar-waktu-${i}`} className="form-input" type="time" value={r.paidAtTime}
                    onChange={(e) => setRowPaidAtTime(i, e.target.value)} />
                </div>
              </div>
              {duplicateNewRowIndexes.has(i) && (
                <div className="form-hint" style={{ color: "var(--red)", marginTop: "var(--gap-xs)" }}>
                  Sudah ada pembayaran dengan metode, tanggal, dan waktu yang sama.
                </div>
              )}
              {!(Number(r.amount) > 0) && (
                <div className="form-hint" style={{ color: "var(--red)", marginTop: "var(--gap-xs)" }}>
                  Jumlah harus lebih dari Rp 0.
                </div>
              )}
              {r.method === "LAINNYA" && (
                <div className="form-row" style={{ marginTop: "var(--gap-sm)", marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`bayar-keterangan-${i}`}>Keterangan</label>
                  <input id={`bayar-keterangan-${i}`} className="form-input" value={r.methodNote || ""} onChange={(e) => setRowNote(i, e.target.value)} />
                </div>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-sm" onClick={addRow}>+ Tambah metode</button>

          {wouldBeLunas && (
            <div className="alert alert--info" style={{ marginTop: "var(--gap-md)" }}>
              Transaksi akan ditandai Lunas.
            </div>
          )}
          {newRows.length > 0 && !paymentsCheck.ok && (
            <div className="form-hint" style={{ color: "var(--red)", marginTop: "var(--gap-sm)" }}>{paymentsCheck.error}</div>
          )}
          {hasBucket && !overAllocCheck.ok && (
            <div className="form-hint" style={{ color: "var(--red)", marginTop: "var(--gap-sm)" }}>
              {overAllocCheck.violations.map(bucketOverAllocationMessage).join(" ")}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Batal</button>
          <button type="button" className="btn btn-primary" disabled={!canSubmit} onClick={submit}>Konfirmasi Bayar</button>
        </div>
      </div>
    </div>
  );
}
